import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos da API ---------------------- */
export type StandbyItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  moeda?: string | null; // 👈 AGORA TEM MOEDA
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

export type StandbyMovAPI = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null; // técnico
    departamento_origem: string | null;
    tipo: string | null; // "standby-venda"
    proposta_venda?: string | null; // 👈 substitui os_numero
    coletor_nome?: string | null;
    criado_em: string; // ISO
  };
  itens: StandbyItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: any;
  data: StandbyMovAPI[];
};

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/comercial";
const URL = `${BASE}/standby`;
const URL_AWAIT = `https://api.eva-googlemarine.com/departamentos/suprimentos/standby/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  // Match direto na string ISO: "2025-09-26T04:57:46.577Z"
  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );
  if (!m) {
    return { data: iso, hora: "" }; // fallback: mostra string crua
  }

  const [, ano, mes, dia, hora, minuto] = m;
  return {
    data: `${dia}/${mes}/${ano}`,
    hora: `${hora}:${minuto}`,
  };
}

function formatBRL(x: number): string {
  return x.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

/** Linha preparada para a Tabela (ordem = seus headers) */
export type StandbyRowUI = [
  string, // Proposta (proposta_venda)
  string, // Tipo (Serviço/Venda)
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Moeda (BRL / USD / etc.)
  string, // Custo Unitário (com símbolo)
  string, // Técnico
  string, // Data
  string // Hora
];

/** Normaliza a resposta da API */
function tipoLabel(tipo: string | null | undefined): string {
  if (!tipo) return "-";
  if (tipo.includes("servico")) return "Serviço";
  if (tipo.includes("venda")) return "Venda";
  return tipo;
}

function flattenToRows(movs: StandbyMovAPI[]): StandbyRowUI[] {
  const rows: StandbyRowUI[] = [];
  for (const m of movs) {
    const { data, hora } = toBRT(m.movimentacao.criado_em);
    const proposta = m.movimentacao.proposta_venda || "-";
    const tipo = tipoLabel(m.movimentacao.tipo);
    const tecnico = m.movimentacao.coletor_nome || "-";

    for (const it of m.itens) {
      const qtd = Number(it.quantidade || 0);
      const custo = Number(it.custo_unitario ?? "0");

      // 👇 Nova lógica de moeda
      const moeda = (it.moeda || "BRL").toUpperCase();
      const simbolo = moeda === "USD" ? "US$" : moeda === "EUR" ? "€" : "R$"; // default BRL

      const custoStr = `${simbolo} ${formatBRL(custo)}`;

      rows.push([
        proposta,
        tipo,
        it.produto.codigo,
        it.produto.nome,
        String(qtd),
        moeda, // 👈 coluna Moeda
        custoStr, // 👈 coluna Custo Unitário com símbolo certo
        tecnico,
        data,
        hora,
      ]);
    }
  }
  return rows;
}

/** ---------------------- Fetchers ---------------------- */
export async function fetchStandbyOnce(): Promise<{
  rows: StandbyRowUI[];
  meta: RespAPI["meta"] | null;
  rawMovs: StandbyMovAPI[]; // mantém bruto pra modais e detalhamento
}> {
  const res = await fetch(URL, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as RespAPI;
  if (!json.ok) return { rows: [], meta: json.meta ?? null, rawMovs: [] };
  return {
    rows: flattenToRows(json.data),
    meta: json.meta ?? null,
    rawMovs: json.data,
  };
}

/** ---------------------- Await (long-poll) ---------------------- */
type AwaitResult = { changed: boolean; versao: number | null };

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${encodeURIComponent(
      since
    )}&timeout_ms=${AWAIT_TIMEOUT_MS}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);
    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useStandbyComercial() {
  const [rows, setRows] = useState<StandbyRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  // 👇 índice para o modal usar (chave = proposta_venda)
  const [rawByProposta, setRawByProposta] = useState<
    Record<
      string,
      { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
    >
  >({});

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;
    try {
      const r = await fetchStandbyOnce();
      setRows(r.rows);
      setMeta(r.meta);

      // monta o índice por PROPOSTA
      const index: Record<
        string,
        { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
      > = {};
      for (const m of r.rawMovs) {
        const proposta = m.movimentacao.proposta_venda || "-";
        if (!index[proposta])
          index[proposta] = { mov: m.movimentacao, itens: [] };
        index[proposta].itens.push(...m.itens);
      }
      setRawByProposta(index);
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const init = await awaitChange(-1);
        if (init && typeof init.versao === "number") {
          versaoRef.current = init.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) await sleep(25);
          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError")
                await sleep(jitter(ERROR_BACKOFF_MS));
            } finally {
              if (awaitCtrlRef.current === ac) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null)
                awaitSupported.current = false;
              await sleep(POLL_FALLBACK_MS);
              continue;
            } else if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);
            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }
            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) console.error("useStandbyComercial loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetching.current) await sleep(25);
    await carregar();
  };

  return { rows, loading, refresh, meta, rawByProposta };
}
